import { isBeneficiaryStateProvider, replaceSpaces, strToFloat, trimTextContent } from 'services/utils';
import { PAYMENT_METHOD } from 'components/common/constants';
import { getConveraRequirements, normalizeRoutingCode } from '../../converaRequirements';

// A missing value stays missing (the field is omitted from the request, as before).
const normalizeSortCode = (value) => (typeof value === 'string' ? normalizeRoutingCode(value) : value);

// TRN-7638: Convera API / Merchant — only what the customer typed, never the IBAN lookup's branch code (our validator
// can't reliably extract e.g. a Brazilian routing code from an IBAN), and nothing while the field is hidden: it is shown
// without an IBAN (Non-IBAN tab) or when the bank country requires it — the same rule as OpenPaydAdvancedForm. A value
// prefilled from a repeated payment / template stays in a field that was never shown, so it is dropped here.
const getConveraSortCode = ({ providerTableData, data }) => {
  const hasIban = !!providerTableData?.recipient_wallet;
  const { isRoutingCodeRequired } = getConveraRequirements({
    provider: data.wallet?.transfer_provider,
    paymentMethod: data.paymentMethod,
    bankCountry: data?.bank_country || providerTableData?.bank_country,
    hasIban
  });

  return !hasIban || isRoutingCodeRequired ? normalizeSortCode(providerTableData?.sort_code) : '';
};

export default ({ providerTableData, data }) => ({
  sender_wallet: (data.wallet && data.wallet?.wallet_number?.trim()) || '',
  amount: strToFloat(providerTableData?.amount?.toString() || ''),
  currency: (data.wallet && data.wallet?.currency?.trim()) || '',
  transaction_recipient: {
    registration_number: providerTableData.registration_number,
    beneficiary_city: providerTableData.beneficiary_city,
    beneficiary_country: providerTableData.beneficiary_country,
    beneficiary_post_code: providerTableData.beneficiary_post_code,
    ...(isBeneficiaryStateProvider(data.wallet?.transfer_provider) && {
      beneficiary_state: providerTableData.beneficiary_state
    }),
    beneficiary_address: providerTableData.beneficiary_address,
    beneficiary: {
      type: providerTableData.recipient_type.toUpperCase(),
      ...(providerTableData.recipient_type === 'Company'
        ? {
            company_name: trimTextContent(providerTableData?.company_name) || ''
          }
        : {
            first_name: trimTextContent(providerTableData?.first_name) || '',
            last_name: trimTextContent(providerTableData?.last_name) || ''
          })
    },
    requisites:
      data.paymentMethod === PAYMENT_METHOD.SWIFT || data.paymentMethod === PAYMENT_METHOD.TARGET2
        ? {
            iban: replaceSpaces(providerTableData?.recipient_wallet) || '',
            bank_country: data?.bank_country?.trim() || providerTableData?.bank_country?.trim(),
            bank_name: data?.bank_name?.trim() || providerTableData?.bank_name?.trim(),
            account_number: data?.account_number?.trim() || providerTableData?.account_number?.trim(),
            bic: data?.bic?.trim() || providerTableData?.bic?.trim(),
            ...(data.paymentMethod === PAYMENT_METHOD.SWIFT && {
              // TRN-7602: "12-34-56" goes as "123456" (the validation ignores spaces and hyphens)
              sort_code: isBeneficiaryStateProvider(data.wallet?.transfer_provider)
                ? getConveraSortCode({ providerTableData, data })
                : normalizeSortCode(data?.sort_code || providerTableData?.sort_code)
            })
          }
        : {
            iban: replaceSpaces(providerTableData?.recipient_wallet) || '',
            bank_country: data?.bank_country?.trim()
          }
  },
  payment_method: data.paymentMethod || '',
  transfer_provider: (data.wallet && data.wallet?.transfer_provider) || '',
  comment: trimTextContent(providerTableData?.comment) || '',
  documents: data.uploadedFiles ? data.uploadedFiles : [],
  ...(providerTableData.payment_purpose && { purpose: providerTableData.payment_purpose })
});
